/**
 * PDF 渲染工具
 * 将PDF页面渲染为Canvas图片，并提取文本位置信息
 */

import { pdfjs } from "react-pdf";
import type {
  TextItem,
  TextMarkedContent,
} from "pdfjs-dist/types/src/display/api";

// 设置 PDF.js worker - 使用本地文件
if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";
}

// PDF页面信息
export interface PdfPage {
  pageNum: number;
  width: number;
  height: number;
  canvas?: HTMLCanvasElement;
  textItems: PdfTextItem[];
}

// PDF文本项
export interface PdfTextItem {
  id: string;
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  fontSize: number;
  fontName: string;
  hasEOL: boolean;
  page: number;
}

/**
 * 渲染PDF页面为Canvas
 * @param removeText - 是否移除文字（用白色矩形覆盖）
 */
export const renderPdfPage = async (
  pdf: pdfjs.PDFDocumentProxy,
  pageNum: number,
  scale: number = 2,
  removeText: boolean = true
): Promise<PdfPage> => {
  const page = await pdf.getPage(pageNum);
  const viewport = page.getViewport({ scale });

  // 创建Canvas
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("无法创建Canvas上下文");
  }

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  // 渲染PDF页面到Canvas
  await page.render({
    canvasContext: context,
    viewport: viewport,
  }).promise;

  // 提取文本内容
  const textContent = await page.getTextContent();
  const textItems: PdfTextItem[] = [];

  textContent.items.forEach((item: TextItem | TextMarkedContent, index) => {
    if ("str" in item) {
      const transform = item.transform;
      // PDF坐标系转换为Canvas坐标系
      const x = transform[4] * scale;
      const y =
        viewport.height - transform[5] * scale - (item.height || 12) * scale;

      textItems.push({
        id: `text-${pageNum}-${index}`,
        text: item.str,
        x,
        y,
        width: (item.width || 0) * scale,
        height: (item.height || 12) * scale,
        fontSize: (item.height || 12) * scale,
        fontName: item.fontName || "sans-serif",
        hasEOL: item.hasEOL || false,
        page: pageNum,
      });
    }
  });

  // 如果需要移除文字，用白色矩形覆盖文本区域
  if (removeText) {
    context.fillStyle = "#ffffff";

    // 合并同一行的文本项，避免多个小矩形
    const mergedItems = mergeTextItemsInLine(textItems);

    mergedItems.forEach((item) => {
      // 添加更大的边距确保完全覆盖文字
      const paddingX = 4;
      const paddingY = 6;
      context.fillRect(
        item.x - paddingX,
        item.y - paddingY,
        item.width + paddingX * 2,
        item.height + paddingY * 2
      );
    });
  }

  return {
    pageNum,
    width: viewport.width,
    height: viewport.height,
    canvas,
    textItems,
  };
};

/**
 * 加载并渲染整个PDF
 * @param removeText - 是否移除文字（用白色矩形覆盖）
 */
export const renderPdf = async (
  pdfBytes: Uint8Array,
  scale: number = 2,
  removeText: boolean = true
): Promise<PdfPage[]> => {
  const pdf = await pdfjs.getDocument({ data: pdfBytes }).promise;
  const pages: PdfPage[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await renderPdfPage(pdf, i, scale, removeText);
    pages.push(page);
  }

  return pages;
};

/**
 * 合并同一行的文本项
 */
export const mergeTextItemsInLine = (items: PdfTextItem[]): PdfTextItem[] => {
  const merged: PdfTextItem[] = [];
  const lineThreshold = 5; // Y坐标差异阈值

  items.forEach((item) => {
    const lastItem = merged[merged.length - 1];

    if (
      lastItem &&
      Math.abs(lastItem.y - item.y) < lineThreshold &&
      !lastItem.hasEOL
    ) {
      // 同一行，合并文本
      lastItem.text += item.text;
      lastItem.width = item.x + item.width - lastItem.x;
      lastItem.hasEOL = item.hasEOL;
    } else {
      // 新行
      merged.push({ ...item });
    }
  });

  return merged;
};

/**
 * 识别简历区块
 */
export const identifyResumeSections = (items: PdfTextItem[]) => {
  const sections = [
    {
      keywords: ["个人信息", "基本信息", "联系方式", "个人概况"],
      name: "个人信息",
    },
    { keywords: ["教育背景", "教育经历", "学历"], name: "教育背景" },
    { keywords: ["工作经验", "工作经历", "实习经历"], name: "工作经验" },
    { keywords: ["项目经验", "项目经历"], name: "项目经验" },
    { keywords: ["专业技能", "技能", "技术栈"], name: "专业技能" },
    { keywords: ["自我评价", "个人简介", "自我介绍"], name: "自我评价" },
    { keywords: ["求职意向", "期望职位"], name: "求职意向" },
  ];

  const result: { name: string; items: PdfTextItem[]; y: number }[] = [];
  let currentSection: { name: string; items: PdfTextItem[]; y: number } | null =
    null;

  // 按Y坐标排序
  const sortedItems = [...items].sort((a, b) => a.y - b.y);

  sortedItems.forEach((item) => {
    // 检查是否是区块标题
    const matchedSection = sections.find((s) =>
      s.keywords.some((kw) => item.text.includes(kw))
    );

    if (matchedSection && item.fontSize > 12) {
      // 保存之前的区块
      if (currentSection) {
        result.push(currentSection);
      }
      // 创建新区块
      currentSection = {
        name: matchedSection.name,
        items: [item],
        y: item.y,
      };
    } else if (currentSection) {
      currentSection.items.push(item);
    } else {
      // 默认区块
      currentSection = {
        name: "其他",
        items: [item],
        y: item.y,
      };
    }
  });

  if (currentSection) {
    result.push(currentSection);
  }

  return result;
};
